import React, { useState } from "react";
import { Overview, PeriodSwitch } from "@/components/ResultsParts";
import { useOfficeResults } from "@/hooks/useBrokerageManage";
import { compactPrice } from "@/helpers/compactPrice";
import { officeAsSources } from "@/helpers/analyticsMerge";
import ResultsCombined from "@/components/ResultsCombined";
import "./OfficeResults.scss";

export const NETWORK_STATS = (networkName) => [
  { key: "networkImpressions", label: "Impressions" },
  { key: "agentViews", label: `Views by ${networkName} agents` },
  { key: "introsReceived", label: "Introductions to your agents' buyers" },
  { key: "introsSent", label: "Introductions your agents sent" },
  { key: "introReplies", label: "Replies to your agents' introductions" },
  { key: "matchesOpened", label: "Matches your agents opened" },
];

export const GITCHA_STATS = [
  { key: "listingViews", label: "Buyer Want‑Listing views on Gitcha.com" },
  { key: "bioOpens", label: "Agent bio visits" },
  { key: "contacts", label: "People who contacted your agents" },
];

const AGENT_COLUMNS = {
  network: [
    { key: "introsReceived", label: "Introductions received" },
    { key: "introsSent", label: "Introductions sent" },
    { key: "matchesOpened", label: "Matches opened" },
  ],
  gitcha: [
    { key: "listingViews", label: "Buyer Want‑Listing views" },
    { key: "bioOpens", label: "Bio visits" },
    { key: "contacts", label: "Contacts" },
  ],
  wins: [
    { key: "bls", label: "BLS wins" },
    { key: "gitcha", label: "Gitcha wins" },
    { key: "volume", label: "Closed volume", money: true },
  ],
};

export const agentsFor = (agents, tab) =>
  (agents || [])
    .map((agent) => ({ ...agent, values: tab === "wins" ? agent.wins || {} : agent[tab] || {} }))
    .filter((agent) => AGENT_COLUMNS[tab].some((column) => Number(agent.values[column.key]) > 0));

export const sortAgents = (rows, sort) =>
  [...rows].sort((a, b) => {
    if (sort.key === "name") {
      const order = String(a.name || "").localeCompare(String(b.name || ""));
      return sort.dir === "asc" ? order : -order;
    }
    const diff = (Number(a.values[sort.key]) || 0) - (Number(b.values[sort.key]) || 0);
    return sort.dir === "asc" ? diff : -diff;
  });

const valueOf = (column, values) =>
  column.money ? (Number(values[column.key]) > 0 ? compactPrice(values[column.key]) : "$0") : Number(values[column.key]) || 0;

const deltaOf = (change) => {
  const current = Number(change?.current) || 0;
  const previous = Number(change?.previous) || 0;
  if (current === 0 && previous === 0) return null;
  const diff = current - previous;
  if (diff === 0) return { text: "Same as the period before", direction: "flat" };
  return { text: `${diff > 0 ? "+" : "−"}${Math.abs(diff)}`, direction: diff > 0 ? "up" : "down" };
};

const Figure = ({ label, value, change }) => {
  const delta = change ? deltaOf(change) : null;
  return (
    <div className="office-results__figure">
      <dt>{label}</dt>
      <dd>
        <span className="office-results__value">{value}</span>
        {delta ? (
          <span className={`office-results__delta is-${delta.direction}`}>
            {delta.text}
            {delta.direction !== "flat" ? <span className="visually-hidden"> vs the period before</span> : null}
          </span>
        ) : null}
      </dd>
    </div>
  );
};

const Segmented = ({ label, items, value, onChange }) => (
  <div className="office-results__switch" role="tablist" aria-label={label}>
    {items.map((item) => (
      <button
        key={item.key}
        type="button"
        role="tab"
        aria-selected={item.key === value}
        className={`office-results__switch-option${item.key === value ? " is-on" : ""}`}
        onClick={() => onChange(item.key)}
      >
        {item.label}
      </button>
    ))}
  </div>
);

const SortHeader = ({ column, sort, onSort, numeric }) => {
  const active = sort.key === column.key;
  return (
    <th scope="col" className={numeric ? "is-num" : undefined} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className={`office-results__sort${active ? " is-on" : ""}`} onClick={() => onSort(column.key)}>
        {column.label}
        <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" className={`office-results__caret is-${active ? sort.dir : "none"}`}>
          <path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </th>
  );
};

const AgentTable = ({ rows, columns, sort, onSort }) => {
  const sorted = sortAgents(rows, sort);
  return (
    <div className="office-results__scroll">
      <table className="office-results__table">
        <thead>
          <tr>
            <SortHeader column={{ key: "name", label: "Agent" }} sort={sort} onSort={onSort} />
            {columns.map((column) => (
              <SortHeader key={column.key} column={column} sort={sort} onSort={onSort} numeric />
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((agent, index) => (
            <tr key={agent.userId} className={index === 0 ? "is-top" : undefined}>
              <th scope="row">{agent.name}</th>
              {columns.map((column) => (
                <td key={column.key} className="is-num">
                  {valueOf(column, agent.values)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UNITS = {
  matchesOpened: ["match opened", "matches opened"],
  introsReceived: ["introduction", "introductions"],
  introsSent: ["introduction", "introductions"],
  introReplies: ["reply", "replies"],
  agentViews: ["view", "views"],
  networkImpressions: ["time shown", "times shown"],
  contacts: ["contact", "contacts"],
  bioOpens: ["bio open", "bio opens"],
  listingViews: ["view", "views"],
};

const OfficeResults = ({ brokerageId, name, networkName = "MLS", onSeePlan, days: fixedDays = null, sources = null }) => {
  const [ownDays, setDays] = useState(30);
  const days = fixedDays || ownDays;
  const [tab, setTab] = useState("network");
  const [sorts, setSorts] = useState({});
  const query = useOfficeResults(brokerageId, days);
  const data = query.data;

  if (!data) {
    return (
      <section className="content-block office-results">
        <div className="title-sec">
          <h4>Office analytics</h4>
        </div>
        {query.isError ? (
          <p className="office-results__state">
            Office analytics did not load.{" "}
            <button type="button" className="office-results__link" onClick={() => query.refetch()}>
              Try again
            </button>
          </p>
        ) : (
          <p className="office-results__state" aria-busy="true">
            Loading Office analytics…
          </p>
        )}
      </section>
    );
  }

  if (data.locked) {
    return (
      <section className="content-block office-results">
        <div className="title-sec">
          <h4>Office analytics</h4>
          <a role="button" tabIndex={0} onClick={onSeePlan} onKeyDown={(event) => event.key === "Enter" && onSeePlan()}>
            See the brokerage plan
          </a>
        </div>
        <p className="office-results__state">
          See {networkName} network activity, Gitcha.com activity and wins for everyone at {name} with the brokerage&nbsp;plan.
        </p>
      </section>
    );
  }

  const tabs = [
    { key: "network", label: `${networkName} network` },
    { key: "gitcha", label: "Gitcha.com" },
    ...(data.wins ? [{ key: "wins", label: "Wins" }] : []),
  ];
  const period = data.days || days;
  const controlled = Array.isArray(sources);
  const shown = controlled
    ? [...tabs.filter((item) => item.key !== "wins" && sources.includes(item.key)).map((item) => item.key), ...(data.wins ? ["wins"] : [])]
    : [tabs.some((item) => item.key === tab) ? tab : "network"];

  const figuresOf = (current) => {
    if (current === "network") {
      return {
        heading: `${networkName} network activity at ${name}`,
        figures: NETWORK_STATS(networkName).map((stat) => ({ ...stat, value: data.network[stat.key].current, change: data.network[stat.key] })),
      };
    }
    if (current === "gitcha") {
      return {
        heading: `Gitcha.com activity at ${name}`,
        figures: GITCHA_STATS.map((stat) => ({ ...stat, value: data.gitcha[stat.key].current, change: data.gitcha[stat.key] })),
      };
    }
    return {
      heading: `Deals and new clients credited at ${name}`,
      figures: data.wins
        ? [
            { key: "bls", label: `${networkName} BLS wins`, value: data.wins.current.bls, change: { current: data.wins.current.bls, previous: data.wins.previous.bls } },
            { key: "gitcha", label: "Deals and clients from Gitcha.com", value: data.wins.current.gitcha, change: { current: data.wins.current.gitcha, previous: data.wins.previous.gitcha } },
            { key: "volume", label: "Closed volume", value: data.wins.current.volume ? compactPrice(data.wins.current.volume) : "$0" },
          ]
        : [],
    };
  };

  const section = (current) => {
    const { heading, figures } = figuresOf(current);
    const rows = agentsFor(data.agents, current);
    const columns = AGENT_COLUMNS[current];
    const sort = sorts[current] || { key: columns[0].key, dir: "desc" };
    const onSort = (key) =>
      setSorts((prev) => ({
        ...prev,
        [current]: { key, dir: sort.key === key ? (sort.dir === "desc" ? "asc" : "desc") : key === "name" ? "asc" : "desc" },
      }));
    return (
      <React.Fragment key={current}>
        <div className="office-results__overview" role={controlled ? undefined : "tabpanel"}>
          <h4 className={controlled ? "office-results__heading" : "visually-hidden"}>{heading}</h4>
          {current === "wins" ? (
            <dl className="office-results__figures" style={{ "--office-figures": figures.length }}>
              {figures.map((figure) => (
                <Figure key={figure.key} label={figure.label} value={figure.value} change={figure.change} />
              ))}
            </dl>
          ) : (
            <Overview
              key={current}
              label={heading}
              days={period}
              series={data.series}
              tiles={figures.map((figure) => ({ ...figure, unit: UNITS[figure.key] || ["", ""] }))}
            />
          )}
        </div>
        <div className="office-results__agents">
          <h5 className="office-results__subhead">By agent</h5>
          {rows.length ? (
            <AgentTable rows={rows} columns={columns} sort={sort} onSort={onSort} />
          ) : (
            <p className="office-results__state">
              No {current === "wins" ? "wins" : "activity"} at {name} in the last {period}&nbsp;days.
            </p>
          )}
        </div>
      </React.Fragment>
    );
  };

  return (
    <section className={`content-block office-results office-results--open${controlled ? " office-results--embedded" : ""}`}>
      {controlled ? null : (
        <div className="office-results__bar">
          <Segmented label="Office analytics" items={tabs} value={shown[0]} onChange={setTab} />
          <PeriodSwitch value={period} onChange={setDays} />
        </div>
      )}
      {shown.map(section)}
      {controlled ? (
        <ResultsCombined bySource={officeAsSources(data, sources)} days={period} networkName={networkName} seeMore={false} />
      ) : null}
    </section>
  );
};

export default OfficeResults;
